import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import {
    Alert,
    Box,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import HubDialog from '../components/common/HubDialog';
import { getHubFieldSx } from '../theme/hubThemes';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { organizationsApi } from '../services/api';
import { HUB_TYPES, flattenAccessibleOrganizations, getHubRoute } from '../utils/hubRoutes';
import { createTopLevelOrganization } from '../utils/organizations';
import LanguageSwitcher from '../components/LanguageSwitcher';
import HubSelectionSection from './HubSelectionSection';
import ForceReauth from '../components/ForceReauth';
import { getHubSectionStyles } from '../theme/hubThemes';
import { HUB_SECTIONS, getCreateDialogCopy } from './hubSelectionUtils';

const HUB_ICONS = {
    [HUB_TYPES.PROJECT_MANAGEMENT]: BusinessCenterIcon,
    [HUB_TYPES.VENDOR]: StorefrontIcon,
    [HUB_TYPES.CUSTOMER]: HomeWorkIcon,
};

export default function HubSelectionPage({ darkMode }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currentUser, organization, selectOrganization } = useAuth();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [dialogHubType, setDialogHubType] = useState(HUB_TYPES.PROJECT_MANAGEMENT);
    const [parentOrg, setParentOrg] = useState(null);
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [reauthRequired, setReauthRequired] = useState(false);

    const loadOrganizations = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await organizationsApi.getAll();
            setOrganizations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setReauthRequired(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOrganizations();
    }, [loadOrganizations]);

    const flatOrganizations = useMemo(
        () => flattenAccessibleOrganizations(organizations),
        [organizations],
    );

    useEffect(() => {
        if (loading || error || flatOrganizations.length !== 1) {
            return undefined;
        }
        const onlyOrg = flatOrganizations[0];
        let cancelled = false;
        (async () => {
            try {
                let selected = onlyOrg;
                if (organization?.id !== onlyOrg.id) {
                    selected = await selectOrganization(onlyOrg.id);
                }
                if (!cancelled) {
                    navigate(getHubRoute(selected || onlyOrg), { replace: true });
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    setError(t('hubs.selectError'));
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [error, flatOrganizations, loading, navigate, organization?.id, selectOrganization, t]);

    const handleSelect = async (org) => {
        setSubmitting(true);
        setError('');
        try {
            const selected = await selectOrganization(org.id);
            navigate(getHubRoute(selected));
        } catch (err) {
            console.error(err);
            setError(t('hubs.selectError'));
        } finally {
            setSubmitting(false);
        }
    };

    const openCreateDialog = (hubType) => {
        setDialogMode('create');
        setDialogHubType(hubType);
        setParentOrg(null);
        setName('');
        setDialogOpen(true);
    };

    const openManagedDialog = (org) => {
        setDialogMode('managed');
        setDialogHubType(org.organization_type);
        setParentOrg(org);
        setName('');
        setDialogOpen(true);
    };

    const handleDialogSubmit = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            if (dialogMode === 'managed' && parentOrg) {
                await organizationsApi.createManaged(parentOrg.id, { name: trimmed });
            } else {
                await createTopLevelOrganization({
                    name: trimmed,
                    organizationType: dialogHubType,
                });
            }
            setDialogOpen(false);
            await loadOrganizations();
        } catch (err) {
            console.error(err);
            setError(t('hubs.createError'));
        } finally {
            setSubmitting(false);
        }
    };

    const dialogCopy = useMemo(
        () => getCreateDialogCopy({ dialogMode, hubType: dialogHubType, parentOrg, t }),
        [dialogMode, dialogHubType, parentOrg, t],
    );

    const dialogStyles = useMemo(
        () => getHubSectionStyles(dialogHubType, darkMode),
        [dialogHubType, darkMode],
    );
    const dialogFieldSx = useMemo(() => getHubFieldSx(dialogStyles), [dialogStyles]);

    const DialogHubIcon = HUB_ICONS[dialogHubType] || BusinessCenterIcon;
    const redirectingToOnlyOrg = !loading && !error && flatOrganizations.length === 1;

    if (reauthRequired) {
        return <ForceReauth />;
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: darkMode
                    ? 'linear-gradient(160deg, #121212 0%, #1a2332 50%, #121212 100%)'
                    : 'linear-gradient(160deg, #f5f7fa 0%, #e8eef5 50%, #f5f7fa 100%)',
                px: 2,
                py: 4,
            }}
        >
            <Box sx={{ width: '100%', maxWidth: 1140, position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                    <LanguageSwitcher />
                </Box>

                <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ mb: 4, pt: 1 }}>
                    <Typography variant="h4" fontWeight={700} color="text.primary">
                        {t('hubs.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
                        {t('hubs.subtitle')}
                    </Typography>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                        {error}
                    </Alert>
                )}

                {loading || redirectingToOnlyOrg ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            gap: 2,
                            justifyContent: 'center',
                            mx: 'auto',
                        }}
                    >
                        {HUB_SECTIONS.map((section) => (
                            <HubSelectionSection
                                key={section.type}
                                hubType={section.type}
                                title={t(section.titleKey)}
                                description={t(section.descriptionKey)}
                                organizations={organizations}
                                currentUserId={currentUser?.uid}
                                darkMode={darkMode}
                                onSelect={handleSelect}
                                onCreateTopLevel={openCreateDialog}
                                onCreateManaged={openManagedDialog}
                            />
                        ))}
                    </Box>
                )}

                <HubDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    hubType={dialogHubType}
                    title={dialogCopy.title}
                    subtitle={
                        dialogMode === 'managed' && parentOrg
                            ? t('hubs.underOrganization', { name: parentOrg.name })
                            : undefined
                    }
                    description={dialogCopy.description}
                    icon={DialogHubIcon}
                    maxWidth="xs"
                    disableClose={submitting}
                    cancelLabel={t('hubs.cancel')}
                    submitLabel={dialogCopy.submit}
                    onSubmit={handleDialogSubmit}
                    submitting={submitting}
                    submitDisabled={!name.trim()}
                >
                    <TextField
                        autoFocus
                        label={t('hubs.nameLabel')}
                        fullWidth
                        size="small"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={submitting}
                        sx={dialogFieldSx}
                    />
                </HubDialog>
            </Box>
        </Box>
    );
}
